import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { Section } from "@/components/site/Section";
import { CTAButton, mailto } from "@/components/site/CTAButton";
import { ConsultationWizard } from "@/components/site/ConsultationWizard";

export const Route = createFileRoute("/services/european-trademark-attorney")({
  head: () => ({
    meta: [
      { title: "European Trademark Attorney for US Brands | Bauer IP" },
      {
        name: "description",
        content:
          "European trademark attorney for US brands. EUIPO filing, oppositions and licensing support — transparent fees, one-day response in English.",
      },
      {
        property: "og:title",
        content: "European Trademark Attorney for US Brands | Bauer IP",
      },
      { property: "og:image", content: "https://bauerip.com/og-us.jpg" },
      { name: "twitter:image", content: "https://bauerip.com/og-us.jpg" },
      {
        property: "og:description",
        content:
          "EUIPO filing strategy, opposition defence and brand licensing support for US companies expanding into Europe.",
      },
      {
        property: "og:url",
        content: "https://bauerip.com/services/european-trademark-attorney",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [
      {
        rel: "canonical",
        href: "https://bauerip.com/services/european-trademark-attorney",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ProfessionalService",
          name: "Bauer IP — European Trademark Attorney",
          url: "https://bauerip.com/services/european-trademark-attorney",
          areaServed: ["EU", "EUIPO"],
          provider: {
            "@type": "Person",
            name: "Karel Bauer",
            jobTitle: "European Trademark Attorney",
          },
          serviceType: [
            "EU trademark filing",
            "EUIPO opposition",
            "Trademark licensing and franchising support",
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: TrademarkPillarPage,
});

const whenToHire = [
  "You are launching a US brand in Europe and need clearance before marketing commits.",
  "Your EUIPO application has been opposed — or you need to oppose someone else's.",
  "A distributor, franchisee or licensee will use your mark in the EU.",
  "Your US identification of goods doesn't map cleanly onto the EUIPO Harmonised Database.",
  "You need to police look-alike marks and counterfeits across the single market.",
];

const filing = [
  {
    title: "Clearance & availability",
    body: "EUIPO and national register searches, plus a realistic read on which prior rights would actually be enforced.",
  },
  {
    title: "EUTM application",
    body: "One filing covering all 27 member states, prosecuted directly before the EUIPO in English.",
  },
  {
    title: "Class strategy (NICE)",
    body: "Specifications built from the Harmonised Database so your US wording survives EUIPO scrutiny.",
  },
  {
    title: "National & Madrid routes",
    body: "Where an EUTM is blocked in one country, national filings or a Madrid designation keep the rest intact.",
  },
];

const oppositions = [
  {
    title: "Opposition defence",
    body: "Responding within the three-month window: cooling-off negotiation, limitation of goods, or defence on the merits.",
  },
  {
    title: "Filing oppositions",
    body: "Stopping conflicting applications before they register and become a permanent obstacle to your brand.",
  },
  {
    title: "Cancellation & non-use",
    body: "Revocation for non-use and invalidity actions to clear blocking registrations off the register.",
  },
  {
    title: "Coexistence agreements",
    body: "Negotiated territorial and sector splits that let both brands trade without recurring conflict.",
  },
];

const licensing = [
  {
    title: "Franchise & master licence support",
    body: "Trademark structure behind master franchise deals: what is licensed, in which territory, under whose control.",
  },
  {
    title: "Distribution agreements",
    body: "Preventing distributors from registering your mark locally and keeping control of goodwill on termination.",
  },
  {
    title: "Licence recordals",
    body: "Recording licences and assignments at the EUIPO so your chain of title stands up in enforcement.",
  },
  {
    title: "Quality-control provisions",
    body: "Use and control clauses that protect the mark's validity and your royalty stream.",
  },
];

const faqs = [
  {
    q: "What is a European trademark attorney?",
    a: "A representative entitled to act before the EUIPO in trade mark matters across the EU. Unlike a US trademark attorney, the role covers EU-wide filing, opposition and cancellation practice rather than USPTO practice.",
  },
  {
    q: "Does my US trademark registration protect me in Europe?",
    a: "No. Trade mark rights are territorial. A USPTO registration gives you no rights in the EU — you need an EU trade mark (EUTM), national filings, or a Madrid designation.",
  },
  {
    q: "How long does an EU trademark application take?",
    a: "Roughly 4-6 months if unopposed. Publication opens a three-month opposition window; an opposition typically adds a year or more.",
  },
  {
    q: "EUIPO or EPO — which office handles trademarks?",
    a: "The EUIPO handles EU trade marks and designs. The EPO handles European patents only. The two are frequently confused by US clients.",
  },
  {
    q: "Should I file before signing a European franchise or licence deal?",
    a: "Yes. The mark is the asset being licensed. Filing first protects your control, your royalty stream and your ability to terminate.",
  },
  {
    q: "Can I use my US goods and services wording at the EUIPO?",
    a: "Often not verbatim. The EUIPO reads specifications literally and prefers Harmonised Database terms; US-style wording usually needs remapping.",
  },
];

const relatedPosts = [
  {
    slug: "eu-trademark-application-us-guide",
    title: "Does Your US Brand Need an EU Trade Mark?",
  },
  {
    slug: "eu-trademark-class-selection-guide",
    title: "EU Trademark Class Selection (NICE) for US Filers",
  },
  {
    slug: "us-trademark-europe-expansion",
    title: "Taking a US Trademark into Europe",
  },
  {
    slug: "epo-vs-euipo-patents-trademarks",
    title: "EPO vs EUIPO: Patents and Trademarks Explained",
  },
];

const consultBody =
  "Hello Karel,\n\nI'd like to discuss European trade mark protection.\n\n— Name:\n— Company:\n— Website:\n— Brand / mark:\n— What I'm planning in Europe (launch, franchise, licensing, distribution, opposition):\n";

function ServiceGrid({ items }: { items: { title: string; body: string }[] }) {
  return (
    <div className="grid gap-px md:grid-cols-2 bg-border">
      {items.map((s) => (
        <div key={s.title} className="bg-background p-8">
          <h3 className="font-serif text-xl text-foreground leading-snug">
            {s.title}
          </h3>
          <p className="mt-3 text-muted-foreground leading-relaxed">{s.body}</p>
        </div>
      ))}
    </div>
  );
}

function TrademarkPillarPage() {
  return (
    <div className="min-h-screen flex flex-col">
      <SiteHeader />

      <main className="flex-1">
        {/* HERO */}
        <section className="border-b border-border">
          <div className="mx-auto max-w-6xl px-6 pt-20 md:pt-28 pb-16 md:pb-20">
            <p className="eyebrow mb-6">EUIPO · For US Brands</p>
            <h1 className="font-serif text-4xl md:text-6xl leading-[1.05] tracking-tight text-foreground max-w-4xl">
              European Trademark Attorney for{" "}
              <span className="italic text-accent-text">US Brands</span>.
            </h1>
            <p className="mt-8 max-w-2xl text-lg md:text-xl text-muted-foreground leading-relaxed">
              EU trade mark filing, opposition work and licensing support —
              handled directly, in English, by a European patent and trade mark
              attorney with his own practice.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <CTAButton
                variant="accent"
                href={mailto({
                  subject: "European trademark attorney — consultation",
                  body: consultBody,
                })}
              >
                Book a Consultation
              </CTAButton>
              <CTAButton
                variant="secondary"
                href={mailto({
                  subject: "EU trade mark filing quote",
                  body: consultBody,
                })}
              >
                Request a Filing Quote
              </CTAButton>
            </div>
          </div>
        </section>

        {/* BUILT FOR US CLIENTS */}
        <Section
          eyebrow="Built for US clients"
          title="A European counterpart to your US trademark counsel."
          intro="Most US brands don't need a second law firm — they need one European representative who answers quickly, explains the EUIPO's logic in US terms, and doesn't route everything through a partner in another time zone."
        >
          <ul className="grid gap-4 md:grid-cols-2">
            {[
              "Direct partner-level work — no associate hand-offs",
              "Replies within one business day, US hours accommodated",
              "Flat, quoted fees for filings and oppositions",
              "Works alongside your existing US trademark counsel",
            ].map((s) => (
              <li key={s} className="border-t border-border pt-4 text-foreground">
                <span className="text-accent-text mr-3">—</span>
                {s}
              </li>
            ))}
          </ul>
        </Section>

        {/* WHEN TO HIRE */}
        <div className="border-t border-border bg-secondary/30">
          <Section
            eyebrow="When to hire"
            title="Typical moments US brands reach out."
          >
            <ul className="grid gap-px md:grid-cols-2 bg-border">
              {whenToHire.map((p, i) => (
                <li key={i} className="bg-background p-8 flex gap-5 text-foreground">
                  <span className="font-serif text-accent-text text-xl">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="leading-relaxed">{p}</span>
                </li>
              ))}
            </ul>
          </Section>
        </div>

        {/* FILING */}
        <Section
          eyebrow="Filing"
          title="EU trade mark filing."
          intro="One EUTM application covers all 27 member states. The work that decides whether it survives happens before filing — clearance and class strategy."
        >
          <ServiceGrid items={filing} />
        </Section>

        {/* OFFICIAL FEES */}
        <div className="border-t border-border bg-secondary/30">
          <Section
            eyebrow="Official EUIPO fees"
            title="What the EUIPO charges — before any attorney fee."
            intro="These are the official fees paid to the EUIPO, current for 2026. My professional fee is quoted fixed per matter before you instruct, so the total budget is known up front."
          >
            <div className="overflow-x-auto border border-border">
              <table className="w-full text-left text-sm md:text-base">
                <thead>
                  <tr className="border-b border-border bg-background">
                    <th className="px-6 py-4 font-serif text-foreground">Step</th>
                    <th className="px-6 py-4 font-serif text-foreground">Official fee</th>
                    <th className="px-6 py-4 font-serif text-foreground">Note for US filers</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border text-muted-foreground">
                  <tr>
                    <td className="px-6 py-4">EUTM application, first class</td>
                    <td className="px-6 py-4 whitespace-nowrap">€850</td>
                    <td className="px-6 py-4">Online filing; one application covers all 27 EU states</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4">Second class</td>
                    <td className="px-6 py-4 whitespace-nowrap">€50</td>
                    <td className="px-6 py-4">Most US brands file in 2–3 classes</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4">Each further class</td>
                    <td className="px-6 py-4 whitespace-nowrap">€150</td>
                    <td className="px-6 py-4">A typical three-class filing totals €1,050 in official fees</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4">Notice of opposition</td>
                    <td className="px-6 py-4 whitespace-nowrap">€320</td>
                    <td className="px-6 py-4">Three-month window after publication of the conflicting mark</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4">Renewal, first class (per 10 years)</td>
                    <td className="px-6 py-4 whitespace-nowrap">€850</td>
                    <td className="px-6 py-4">Plus €50 / €150 per further class; no proof-of-use filings between renewals</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mt-6 text-muted-foreground leading-relaxed max-w-3xl">
              US applicants without a seat in the European Economic Area must be represented
              before the EUIPO — I handle the representation, the filing and all EUIPO
              correspondence directly, and report in US business hours.{" "}
              <Link to="/eu-trademarks" className="underline hover:text-accent-text">
                The full step-by-step process is in the EU trade mark hub
              </Link>
              .
            </p>
          </Section>
        </div>

        {/* OPPOSITIONS */}
        <div className="border-t border-border bg-secondary/30">
          <Section
            eyebrow="Oppositions"
            title="Oppositions, cancellations and coexistence."
            intro="Publication opens a three-month window in both directions. Most disputes are settled by negotiation once the legal position is clear."
          >
            <ServiceGrid items={oppositions} />
          </Section>
        </div>

        {/* LICENSING */}
        <Section
          eyebrow="Licensing"
          title="Licensing, franchising and distribution support."
          intro="When your brand expands through partners, the trade mark is the asset under contract. Structure it before signing, not after a dispute."
        >
          <ServiceGrid items={licensing} />
        </Section>

        {/* RELATED READING */}
        <div className="border-t border-border bg-secondary/30">
          <Section eyebrow="Related reading" title="Guides for US filers.">
            <ul className="grid gap-4 md:grid-cols-2">
              {relatedPosts.map((p) => (
                <li key={p.slug} className="border-t border-border pt-4">
                  <Link
                    to="/blog/$slug"
                    params={{ slug: p.slug }}
                    className="text-foreground hover:text-accent-text transition-colors"
                  >
                    <span className="text-accent-text mr-3">—</span>
                    {p.title}
                  </Link>
                </li>
              ))}
            </ul>
            <p className="mt-8 text-muted-foreground">
              Filing an EUTM yourself first? Start with the{" "}
              <Link
                to="/eu-trademarks"
                className="text-accent-text underline underline-offset-4"
              >
                EU trade mark filing hub
              </Link>{" "}
              — process, official EUIPO fees, timeline and common refusals.
            </p>
            <p className="mt-4 text-muted-foreground">
              Filing patents too? See the{" "}
              <Link
                to="/services/european-patent-attorney"
                className="text-accent-text underline underline-offset-4"
              >
                European patent attorney service page
              </Link>
              .
            </p>
            <p className="mt-4 text-muted-foreground">
              US firm instructing on behalf of a client? See{" "}
              <Link
                to="/services/foreign-associate-for-us-firms"
                className="text-accent-text underline underline-offset-4"
              >
                foreign associate services for US firms
              </Link>
              .
            </p>

          </Section>
        </div>

        {/* FAQ */}
        <Section eyebrow="FAQ" title="Common questions from US brands.">
          <dl className="divide-y divide-border border-y border-border">
            {faqs.map((f) => (
              <div key={f.q} className="grid md:grid-cols-[1fr_2fr] gap-6 py-8">
                <dt className="font-serif text-lg text-foreground">{f.q}</dt>
                <dd className="text-muted-foreground leading-relaxed">{f.a}</dd>
              </div>
            ))}
          </dl>
        </Section>

        {/* CTA */}
        <section className="border-t border-border bg-primary text-primary-foreground">
          <div className="mx-auto max-w-6xl px-6 py-20 md:py-24">
            <h2 className="font-serif text-3xl md:text-5xl leading-tight max-w-3xl">
              Ready to protect your brand in Europe?
            </h2>
            <p className="mt-6 text-lg text-primary-foreground/90 max-w-2xl">
              Send your mark, your target markets and what you&rsquo;re planning
              in Europe. You&rsquo;ll get a straight answer and a fee estimate,
              usually the next business day.
            </p>
            <div className="mt-10 grid gap-10 lg:grid-cols-[1.1fr_1fr] items-start text-foreground">
              <ConsultationWizard
                matter="trademark"
                context="trademark_pillar_final_cta"
              />
              <div className="text-primary-foreground">
                <p className="text-sm text-primary-foreground/80 mb-4">
                  Prefer to just email?
                </p>
                <CTAButton
                  variant="accent"
                  href={mailto({
                    subject: "European trademark attorney — new matter",
                    body: consultBody,
                  })}
                  trackLabel="trademark_pillar_email_direct"
                >
                  Book a Consultation
                </CTAButton>
              </div>
            </div>

          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
